import React, { useCallback, useEffect, useRef, useState } from 'react';

import Draggable from 'common/Draggable';
import { Span } from 'common/ui/Text';
import styles from 'css-module/components/ui/_Slider.module.scss';

const SliderHandleSize = 16;
const SliderPadding = SliderHandleSize / 2;

type Props = {
  onChange: (value: string) => void;
  value: string;
  values: string[];
};

function calculateHandlePercentForValue(value: string, values: string[]) {
  const valueIndex = values.indexOf(value);
  return valueIndex / (values.length - 1);
}

export default function Slider({ onChange, value, values }: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const [handlePosition, setHandlePosition] = useState(SliderPadding);
  const [sliderRect, setSliderRect] = useState<DOMRect | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const calculateNewValueFromHandlePosition = useCallback(
    (newHandlePosition: number) => {
      if (!sliderRect) {
        return;
      }

      const handleMin = SliderPadding;
      const handleMax = sliderRect.width - SliderPadding;
      const newHandlePercent = (newHandlePosition - handleMin) / (handleMax - handleMin);
      const newValueIndex = Math.round(newHandlePercent * (values.length - 1));
      const newValue = values[newValueIndex];

      if (newValue !== value) {
        onChange(newValue);
      }
    },
    [onChange, sliderRect, value, values],
  );

  const onBarClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const slider = sliderRef.current;
      if (!slider) {
        return;
      }

      const sliderRect = slider.getBoundingClientRect();
      const handlePosition = e.clientX - sliderRect.left;
      calculateNewValueFromHandlePosition(handlePosition);
    },
    [sliderRef, calculateNewValueFromHandlePosition],
  );

  const onResize = useCallback(() => {
    const slider = sliderRef.current;
    if (!slider) {
      return;
    }

    if (isDragging) {
      return;
    }

    const sliderRect = slider.getBoundingClientRect();
    setSliderRect(sliderRect);

    const snapHandlePercent = calculateHandlePercentForValue(value, values);
    const snappedHandlePosition = Math.round(
      snapHandlePercent * (sliderRect.width - 2 * SliderPadding) + SliderPadding,
    );
    setHandlePosition(snappedHandlePosition);
  }, [isDragging, value, values]);

  const onDragEnd = useCallback(() => {
    setIsDragging(false);

    if (!sliderRect) {
      return;
    }

    const snapHandlePercent = calculateHandlePercentForValue(value, values);
    const snappedHandlePosition = Math.round(
      snapHandlePercent * (sliderRect.width - 2 * SliderPadding) + SliderPadding,
    );
    setHandlePosition(snappedHandlePosition);
  }, [value, values, sliderRect]);

  const onDragMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!sliderRect) {
        return;
      }

      let pageX: number;
      if (e instanceof MouseEvent) {
        if (!e.pageX) {
          return;
        }

        pageX = e.pageX;
      } else if (e instanceof TouchEvent) {
        if (!e.touches) {
          return;
        }

        pageX = e.touches[0].pageX;
      } else {
        return;
      }

      const newHandlePosition = Math.round(
        Math.max(
          SliderPadding,
          Math.min(sliderRect.width - SliderPadding, pageX - sliderRect.left),
        ),
      );
      if (newHandlePosition !== handlePosition) {
        setHandlePosition(newHandlePosition);
        calculateNewValueFromHandlePosition(newHandlePosition);
      }
    },
    [handlePosition, sliderRect, calculateNewValueFromHandlePosition],
  );

  const onDragStart = useCallback(() => {
    setIsDragging(true);
  }, [setIsDragging]);

  // on window resize, recalculate slider rect and handle position
  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize, false);

    return () => {
      window.removeEventListener('resize', onResize, false);
    };
  }, [onResize]);

  // while dragging, set the cursor to grabbing
  useEffect(() => {
    if (isDragging) {
      document.body.classList.add('dragging');
    } else {
      document.body.classList.remove('dragging');
    }
  }, [isDragging]);

  const minLabel = values[0];
  const maxLabel = values[values.length - 1];
  const valueIsMinOrMax = value === minLabel || value === maxLabel;
  return (
    <div className={styles.slider} data-is-dragging={isDragging}>
      <div className={styles.bar} onClick={onBarClick} ref={sliderRef}>
        <div className={styles.barVisible}>
          <div className={styles.barFill} style={{ width: handlePosition }} />
        </div>
        <Draggable onDragEnd={onDragEnd} onDragMove={onDragMove} onDragStart={onDragStart}>
          <div className={styles.handle} style={{ left: handlePosition }} />
        </Draggable>
      </div>
      <div className={styles.labels}>
        <Span className={styles.label} variant="bodySm">
          {minLabel}
        </Span>
        {!valueIsMinOrMax && (
          <Span
            className={styles.label}
            variant="bodySm"
            data-is-handle-label={true}
            style={{ left: handlePosition }}>
            {value}
          </Span>
        )}
        <Span className={styles.label} variant="bodySm">
          {maxLabel}
        </Span>
      </div>
    </div>
  );
}
